import type { FieldErrors, UseFormRegister } from 'react-hook-form'
import { CheckboxGroup } from '@/components/forms/ui/checkbox-group'
import { FieldBlock } from '@/components/forms/ui/field-block'
import { Input } from '@/components/forms/ui/input'
import { Select } from '@/components/forms/ui/select'
import { Textarea } from '@/components/forms/ui/textarea'
import { ValidationError } from '@/components/forms/ui/validation-error'
import { Checkbox } from '@/components/parts/checkbox'
import type { Department } from '@/types/microcms/department'
import type { FormField } from '@/types/microcms/seminar'

export type SeminarFormValues = {
  salesforceId: string
  first_view_route: string
  last_view_route: string
  /** checkbox-group は選んだ値の配列（未選択は false）、checkbox は真偽値になる */
  [key: string]: string | string[] | boolean | undefined
}

/** 定例セミナーの開催日程。select_form5 の選択肢になる */
export type SeminarScheduleItem = {
  fieldId: string
  preferredDay: string
  salesforceId: string
}

type SeminarFormFieldProps = {
  /** microCMS の seminar.form の 1 項目 */
  form: FormField
  /** 姓（lastname）の直後に名（firstname）があるかを見るため、項目の並びと位置を受け取る */
  index: number
  forms: FormField[]
  register: UseFormRegister<SeminarFormValues>
  errors: FieldErrors<SeminarFormValues>
  departments?: Department[]
  scheduleItems: SeminarScheduleItem[]
  /** 参加希望日を選んだとき、その日程の Pardot のフォームハンドラを送信先にする */
  onSalesforceIdChange: (salesforceId: string) => void
}

/**
 * セミナー申込フォームの 1 項目。Figma: form-field。
 * microCMS の fieldId / fieldname から入力部品を選ぶ。名（firstname）は姓の行で描くので null を返す。
 */
export function SeminarFormField({
  form,
  index,
  forms,
  register,
  errors,
  departments,
  scheduleItems,
  onSalesforceIdChange
}: SeminarFormFieldProps) {
  const { fieldId, fieldname, label = '', placeholder = '' } = form
  const inputType = form.type?.[0] || '' // HTML input type (text, email, etc.)
  const isRequired = form.required?.[0]?.required || false

  if (fieldname === 'select_form5' || fieldId === 'select_form5') {
    return (
      <FieldBlock
        labelText={label || '参加希望日'}
        htmlFor={fieldname}
        required={true}
        error={errors[fieldname]}
      >
        <Select
          id={fieldname}
          register={register(fieldname, {
            required: '必須項目です',
            onChange: (e) => {
              const selectedSchedule = scheduleItems.find(
                (s) => s.fieldId === e.target.value
              )
              if (selectedSchedule) {
                // セールスフォースIDを設定
                onSalesforceIdChange(
                  selectedSchedule.salesforceId.replace(
                    'https://info.lawgue.com',
                    ''
                  )
                )
                const formElement = e.target.form
                if (formElement) {
                  const selectInput = formElement.elements.namedItem(
                    'select_form5'
                  ) as HTMLInputElement
                  if (selectInput) {
                    selectInput.setAttribute(
                      'data-text',
                      selectedSchedule.preferredDay
                    )
                  }
                }
              }
            }
          })}
          options={[
            { label: '選択してください', value: '' },
            ...scheduleItems.map((schedule) => ({
              label: schedule.preferredDay,
              value: schedule.fieldId
            }))
          ]}
        />
      </FieldBlock>
    )
  }

  // 姓・名は横に並べる（Figma: form の row）
  if (fieldname === 'lastname') {
    const nextField = forms[index + 1]
    if (nextField && nextField.fieldname === 'firstname') {
      const nextIsRequired = nextField.required?.[0]?.required || false
      return (
        <div className="flex gap-4">
          <div className="min-w-0 flex-1">
            <FieldBlock
              labelText={label}
              htmlFor={fieldname}
              required={isRequired}
              error={errors[fieldname]}
            >
              <Input
                id={fieldname}
                type="text"
                placeholder={placeholder}
                autoComplete="family-name"
                register={register(fieldname, {
                  required: isRequired ? '必須項目です' : false
                })}
              />
            </FieldBlock>
          </div>
          <div className="min-w-0 flex-1">
            <FieldBlock
              labelText={nextField.label}
              htmlFor={nextField.fieldname}
              required={nextIsRequired}
              error={errors[nextField.fieldname]}
            >
              <Input
                id={nextField.fieldname}
                type="text"
                placeholder={nextField.placeholder}
                autoComplete="given-name"
                register={register(nextField.fieldname, {
                  required: nextIsRequired ? '必須項目です' : false
                })}
              />
            </FieldBlock>
          </div>
        </div>
      )
    }
  }

  if (fieldname === 'firstname') return null

  if (fieldId === 'department-select') {
    // microCMS で fieldname が空のときは fieldId を名前に使う。
    // ラベル・エラー・登録名を同じ名前にそろえないと、空の名前で登録されて値が送られない
    const name = fieldname || fieldId
    return (
      <FieldBlock
        labelText={label || '部署区分'}
        htmlFor={name}
        required={isRequired}
        error={errors[name]}
      >
        <Select
          id={name}
          register={register(name, {
            required: isRequired ? '必須項目です' : false
          })}
          options={[
            { label: '選択してください', value: '' },
            ...(departments?.map((dept) => ({
              label: dept.title,
              value: dept.title
            })) || [])
          ]}
        />
      </FieldBlock>
    )
  }

  // 複数選択のチェックボックス。選択肢は microCMS の select から作る。
  // 送信時に index.tsx でセミコロン区切りの 1 つの文字列にする（Salesforce の複数選択リストの形）
  if (fieldId === 'checkbox-group') {
    const name = fieldname || fieldId
    return (
      <FieldBlock
        labelText={label}
        htmlFor={`${name}_0`}
        required={isRequired}
        error={errors[name]}
      >
        <CheckboxGroup
          id={name}
          register={register(name, {
            // 未選択は RHF から [] か false が渡る
            validate: isRequired
              ? (value) =>
                  (Array.isArray(value) ? value.length > 0 : Boolean(value)) ||
                  '1つ以上選択してください'
              : undefined
          })}
          options={(form.select || []).map((option) => ({
            label: option,
            value: option
          }))}
        />
      </FieldBlock>
    )
  }

  // 個人情報の取り扱いへの同意。ラベル行は持たない（Figma: form-field Type=checkbox）
  if (fieldId === 'checkbox') {
    return (
      <div className={errors[fieldname] ? 'error_input' : undefined}>
        <Checkbox
          htmlfor={fieldname}
          register={register}
          required={isRequired ? 'チェックを入れてください' : false}
        />
        <ValidationError error={errors[fieldname]} />
      </div>
    )
  }

  return (
    <FieldBlock
      labelText={label}
      htmlFor={fieldname}
      required={isRequired}
      error={errors[fieldname]}
    >
      {fieldId === 'input' && (
        <Input
          id={fieldname}
          type={inputType || 'text'}
          placeholder={placeholder}
          register={register(fieldname, {
            required: isRequired ? '必須項目です' : false,
            pattern:
              fieldname === 'email'
                ? {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: '入力されたメールアドレスに間違いがあります'
                  }
                : fieldname === 'phone_number' || fieldname === 'tel'
                  ? {
                      value: /^\d{2,4}-\d{1,4}-\d{4}$/,
                      message: '電話番号の形式を確認してください'
                    }
                  : undefined
          })}
        />
      )}
      {fieldId === 'select' && (
        <Select
          id={fieldname}
          register={register(fieldname, {
            required: isRequired ? '必須項目です' : false
          })}
          options={[
            { label: '選択してください', value: '' },
            ...(form.options ||
              form.select?.map((option) => ({
                label: option,
                value: option
              })) ||
              [])
          ]}
        />
      )}
      {fieldId === 'textarea' && (
        <Textarea
          id={fieldname}
          placeholder={placeholder || ''}
          register={register(fieldname, {
            required: isRequired ? '必須項目です' : false
          })}
        />
      )}
    </FieldBlock>
  )
}
